/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

import { IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification/src/modules/product-specification/./product-specification';
import { IProductSpecificationProps } from '../definition-extensions/product-specification.ext.props.autogenerated';
// import { IoCloseSharp } from 'react-icons/io5';

const ProductSpecificationView: React.FC<IProductSpecificationViewProps & IProductSpecificationProps<{}>> = props => {
    const { ProductSpecification, ProductSpecificationTableProps, ProductSpecificationTableBodyProps, productSpecificationResult } = props;
    const rowdata =
        productSpecificationResult &&
        ArrayExtensions.hasElements(productSpecificationResult) &&
        productSpecificationResult.filter(row => !ObjectExtensions.isNullOrUndefined(row));
    let renderAsTag = props.config.renderAsTag;

    const [isTagEnabled, setIsTagEnabled] = useState(false);
    console.log(isTagEnabled);

    return rowdata && ArrayExtensions.hasElements(rowdata) ? (
        renderAsTag ? (
            <div className='moe-tags-wrap'>
                <button onClick={() => setIsTagEnabled(!isTagEnabled)} className='msc-btn msc-btn--tags moe-btn-tags'>
                    <i className='fas fa-tags'></i> Tags
                </button>
                <div className={`msc-moe-product-specification-tag ${isTagEnabled ? 'tags-show' : 'tags-hide'}`}>
                    <div className='msc-moe-tag-main-container'>
                        {props.title}
                        <span className='msc-tag-divider'></span>
                        <ul className='msc-moe-tag-container'>{_renderAsTag(props)}</ul>
                    </div>
                </div>
            </div>
        ) : (
            <Module {...ProductSpecification}>
                {props.title}
                <Node {...ProductSpecificationTableProps}>
                    <Node {...ProductSpecificationTableBodyProps}>{rowdata}</Node>
                </Node>
            </Module>
        )
    ) : null;
};

const _renderAsTag = (props: any): (JSX.Element | null)[] => {
    return props.data.productSpecificationData.result!.map((product: AttributeValue, index: any): any => {
        const cellData: JSX.Element | null = _renderProductCell(product, props);
        let isRefinable: boolean | undefined = false;
        // let isRetrievable: boolean | undefined = false;

        product?.ExtensionProperties?.forEach(item => {
            if (item.Key === 'IsRefinable') {
                isRefinable = item.Value?.BooleanValue;
            }
            // if (item.Key === 'IsRetrievable') {
            //     isRetrievable = item.Value?.BooleanValue;
            // }
        });

        if (isRefinable) {
            if (product.TextValue) {
                return (
                    <li>
                        <span className='tag-product-name'>{product.Name}</span>
                        {cellData}
                    </li>
                );
            }
        }
    });
};

const _renderProductCell = (product: AttributeValue, props: IProductSpecificationViewProps): JSX.Element | null => {
    switch (product.DataTypeValue) {
        case 0: // None
            // Return span here instead of null so the row still shows
            return <span />;
        case 1: // Currency
            return _renderCurrencyCell(product);
        case 2: // DateTime
            return _renderDateTimeCell(product);
        case 3: // Decimal
            return _renderDecimalCell(product);
        case 4: // Integer
            return _renderIntegerCell(product);
        case 5: // Text
            return _renderTextCell(product);
        case 6: // TrueFalse
            return _renderTrueFalseCell(product, props);
        default:
            //props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
            return null;
    }
};

const _renderCurrencyCell = (product: AttributeValue): JSX.Element | null => {
    if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
        return (
            <span className='tag-cell-data'>
                {product.CurrencyCode}
                {product.CurrencyValue}
            </span>
        );
    }

    return null;
};

const _renderDateTimeCell = (product: AttributeValue): JSX.Element | null => {
    if (product.DateTimeOffsetValue?.toUTCString) {
        return <span className='tag-cell-data'>{product.DateTimeOffsetValue.toUTCString()}</span>;
    }

    return null;
};

const _renderIntegerCell = (product: AttributeValue): JSX.Element | null => {
    if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
        return <span className='tag-cell-data'>{product.IntegerValue}</span>;
    }

    return null;
};

const _renderDecimalCell = (product: AttributeValue): JSX.Element | null => {
    if (product.FloatValue !== null && product.FloatValue !== undefined) {
        return <span className='tag-cell-data'>{product.FloatValue}</span>;
    }

    return null;
};

const _renderTextCell = (product: AttributeValue): JSX.Element | null => {
    if (product.TextValue !== null && product.TextValue !== undefined) {
        const span = product.TextValue.split(';').map((item: string, idx: number) => {
            if (item) {
                return <span key={idx} className='tag-cell-data' dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(item)}></span>;
            } else {
                return <></>;
            }
        });
        return <>{span}</>;
    }

    return null;
};

const _renderTrueFalseCell = (product: AttributeValue, props: IProductSpecificationViewProps): JSX.Element | null => {
    // TODO 20369885 setting to default value is needed to work around a platform bug where
    // config string value does not get its default value.
    if (product.BooleanValue) {
        return <span>{props.resources.trueValueText ? props.resources.trueValueText : 'Yes'}</span>;
    }
    return <span className='tag-cell-data'>{props.resources.falseValueText ? props.resources.falseValueText : 'No'}</span>;
};

export default ProductSpecificationView;
